/** Names, hints and colours for generated caves: word lists and curated palettes. */

import type { Rng } from '../../sim/rng';
import type { CaveColors } from '../types';
import type { FeatureId } from './plan';

const ADJECTIVES = [
  'AMBER', 'ASHEN', 'BLACK', 'BROKEN', 'BURIED', 'COLD', 'COPPER', 'CRIMSON', 'DUSTY', 'ECHOING',
  'EMBER', 'FALLEN', 'FLINT', 'GILDED', 'GLOOMY', 'GRANITE', 'HIDDEN', 'IRON', 'JAGGED', 'LOST',
  'MOSSY', 'MUDDY', 'OLD', 'QUIET', 'RUSTY', 'SALT', 'SHALE', 'SILENT', 'SLATE', 'SUNKEN',
  'TWISTED', 'UMBER', 'VELVET', 'WEEPING', 'WILD', 'WINDING', 'DEEP', 'DRY', 'PALE', 'RED',
];

const NOUNS = [
  'HOLLOW', 'GALLERY', 'SEAM', 'SHAFT', 'VEIN', 'WARREN', 'GROTTO', 'CAVERN', 'BURROW', 'DELVE',
  'DEPTHS', 'PIT', 'CHAMBER', 'TUNNELS', 'HALLS', 'REACH', 'DROP', 'FAULT', 'LODE', 'RIFT',
  'CRYPT', 'NEST', 'QUARRY', 'SPUR', 'WORKINGS', 'MAZE', 'DEN', 'CELLAR', 'GORGE', 'CHASM', 'TROVE',
];

const THE_NOUNS = [
  'GULLET', 'MAW', 'SUMP', 'CRUCIBLE', 'FURNACE', 'BELLOWS', 'UNDERCROFT', 'ANVIL', 'SIEVE', 'ROOKERY',
  'LARDER', 'GRINDER', 'KNOT', 'WELL', 'THROAT', 'SPINDLE', 'CRADLE', 'HIVE', 'ORCHARD', 'MILLRACE',
];

/** Whose place it is: "MAGPIE" becomes "MAGPIE'S". */
const OWNERS = ['MOLE', 'MINER', 'MAGPIE', 'HERMIT', 'TINKER', 'GIANT', 'DIGBY', 'WIDOW', 'KING'];
const OWNED = ['REST', 'FOLLY', 'LUCK', 'HOARD', 'PURSE', 'DOOR', 'CUPBOARD', 'LEDGE', 'RUIN', 'DREAM'];

/** Words that tell of a cave's headline feature. */
const THEMES: Partial<Record<FeatureId, readonly string[]>> = {
  firefly: ['EMBERS', 'SPARKS', 'LANTERNS', 'SENTRIES'],
  butterfly: ['MOTHS', 'WINGS', 'FLUTTER'],
  amoeba: ['OOZE', 'BLOOM', 'MOULD', 'TIDE'],
  magic: ['ALCHEMY', 'WONDERS', 'MIRACLE'],
  expand: ['CREEPERS', 'CLOSE', 'SQUEEZE'],
  slime: ['SEEP', 'DRIP', 'MIRE'],
  vault: ['STRONGROOM', 'LOCKS', 'COFFERS'],
  clocks: ['HOURS', 'MINUTES'],
  shelf: ['LEDGES', 'SLIDE'],
};

const HINTS: Record<FeatureId, string> = {
  room: 'Brick rooms hide gems. Find the doors.',
  wall: 'Long walls, narrow gaps. Plan a route.',
  shelf: 'Dig beside a shelf and its boulders roll.',
  cache: 'A hoard lies buried. Mind what sits on it.',
  firefly: 'Fireflies circle their tracks. Time it.',
  butterfly: 'Drop a boulder on a butterfly for gems.',
  amoeba: 'The amoeba is waking. Be quick.',
  magic: 'Drop boulders through the magic wall.',
  expand: 'Walls creep after you. Grab and go.',
  slime: 'Slime lets rocks and gems seep through.',
  vault: 'Find the key. The vault holds the riches.',
  clocks: 'Clocks buy you ten more seconds.',
};

/** Warm earth for Classic depths; the Deep leans colder and darker (docs/spec/03 §3). */
const WARM: readonly CaveColors[] = [
  [8, 12, 1], [9, 15, 7], [2, 15, 7], [8, 14, 7], [9, 10, 7], [5, 12, 7], [4, 3, 1],
  [2, 12, 3], [11, 10, 7], [9, 12, 3], [8, 15, 7], [5, 15, 1], [4, 13, 7], [11, 14, 1],
];
const COLD: readonly CaveColors[] = [
  [6, 3, 1], [6, 13, 7], [11, 14, 1], [11, 3, 7], [6, 15, 3], [4, 14, 3], [11, 13, 5],
  [6, 12, 1], [2, 14, 3], [9, 3, 1], [11, 15, 3],
];

function pick<T>(rng: Rng, list: readonly T[], fallback: T): T {
  return list[rng.int(list.length)] ?? fallback;
}

/** "EMBER EMBERS": two words from one stem. */
function echoes(name: string): boolean {
  const [a, b] = name.split(' ');
  return a !== undefined && b !== undefined && b.startsWith(a.slice(0, 4));
}

/** An evocative name of at most 16 characters: "AMBER HOLLOW", "THE GULLET", "MAGPIE'S HOARD". */
export function caveName(rng: Rng, theme: FeatureId | undefined): string {
  const themed = theme === undefined ? undefined : THEMES[theme];
  for (let i = 0; i < 12; i++) {
    const r = rng.int(100);
    let name: string;
    if (themed !== undefined && r < 30) name = `${pick(rng, ADJECTIVES, 'OLD')} ${pick(rng, themed, 'DEEP')}`;
    else if (r < 60) name = `${pick(rng, ADJECTIVES, 'OLD')} ${pick(rng, NOUNS, 'HOLLOW')}`;
    else if (r < 85) name = `THE ${pick(rng, THE_NOUNS, 'GULLET')}`;
    else name = `${pick(rng, OWNERS, 'MOLE')}'S ${pick(rng, OWNED, 'REST')}`;
    if (name.length <= 16 && !echoes(name)) return name;
  }
  return pick(rng, NOUNS, 'HOLLOW');
}

/** One line for the cave select: the quota, then what to look out for, in at most 60 characters. */
export function caveHint(theme: FeatureId | undefined, quota: number): string {
  const tip = theme === undefined ? 'Dig for diamonds and find the exit.' : HINTS[theme];
  const full = `Find ${quota} diamonds. ${tip}`;
  return full.length <= 60 ? full : tip;
}

export function caveColors(rng: Rng, deep: boolean): CaveColors {
  return pick(rng, deep ? COLD : WARM, [8, 12, 1]);
}
